import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';

const TWEET_URL_REGEX = /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+\/status\/\d+/i;

function StaffArticleActions({ articleId, userData, onNoteAdded }) {
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showImportantModal, setShowImportantModal] = useState(false);
    const [showDiscordModal, setShowDiscordModal] = useState(false);
    const [showTwitterModal, setShowTwitterModal] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [importantText, setImportantText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Twitter/X modal state
    const [twitterUrl, setTwitterUrl] = useState('');
    const [twitterText, setTwitterText] = useState('');
    const [twitterUrlError, setTwitterUrlError] = useState('');

    // Discord modal state
    const [discordText, setDiscordText] = useState('');
    const [discordImageFile, setDiscordImageFile] = useState(null);
    const [discordImagePreview, setDiscordImagePreview] = useState(null);
    const [discordImageUrl, setDiscordImageUrl] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    const handleAddNote = async (isImportant) => {
        const text = isImportant ? importantText : noteText;

        if (!text.trim()) {
            toast.error('Note cannot be empty');
            return;
        }

        try {
            setIsSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/${articleId}/notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    noteText: text.trim(),
                    importantFlag: isImportant ? 1 : 0
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(isImportant ? 'Important note added!' : 'Note added!');
                if (isImportant) {
                    setImportantText('');
                    setShowImportantModal(false);
                } else {
                    setNoteText('');
                    setShowNoteModal(false);
                }
                if (onNoteAdded) onNoteAdded();
            } else {
                toast.error(data.message || 'Failed to add note');
            }
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Failed to add note');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDiscordImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setDiscordImageFile(file);
        setDiscordImagePreview(URL.createObjectURL(file));
        setDiscordImageUrl(null);

        try {
            setIsUploadingImage(true);
            const formData = new FormData();
            formData.append('image', file);

            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const data = await response.json();

            if (data.url) {
                setDiscordImageUrl(data.url);
                toast.success('Image uploaded');
            } else {
                toast.error(data.message || 'Image upload failed');
                setDiscordImageFile(null);
                setDiscordImagePreview(null);
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Image upload failed');
            setDiscordImageFile(null);
            setDiscordImagePreview(null);
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleDiscordSubmit = async () => {
        if (!discordText.trim()) {
            toast.error('Please add some text describing the Discord details');
            return;
        }
        if (!discordImageUrl) {
            toast.error('Please upload an image before submitting');
            return;
        }

        try {
            setIsSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/${articleId}/notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    noteText: discordText.trim(),
                    importantFlag: 0,
                    noteType: 'discord',
                    noteImageUrl: discordImageUrl
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Discord details added!');
                setDiscordText('');
                setDiscordImageFile(null);
                setDiscordImagePreview(null);
                setDiscordImageUrl(null);
                setShowDiscordModal(false);
                if (onNoteAdded) onNoteAdded();
            } else {
                toast.error(data.message || 'Failed to add Discord details');
            }
        } catch (error) {
            console.error('Error adding Discord note:', error);
            toast.error('Failed to add Discord details');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseDiscordModal = () => {
        if (isSubmitting || isUploadingImage) return;
        setDiscordText('');
        setDiscordImageFile(null);
        setDiscordImagePreview(null);
        setDiscordImageUrl(null);
        setShowDiscordModal(false);
    };

    const handleTwitterUrlChange = (e) => {
        const val = e.target.value;
        setTwitterUrl(val);
        if (val && !TWEET_URL_REGEX.test(val)) {
            setTwitterUrlError('Please enter a valid X/Twitter post URL');
        } else {
            setTwitterUrlError('');
        }
    };

    const handleTwitterSubmit = async () => {
        if (!twitterUrl.trim()) {
            toast.error('A tweet URL is required');
            return;
        }
        if (!TWEET_URL_REGEX.test(twitterUrl.trim())) {
            toast.error('Please enter a valid X/Twitter post URL');
            return;
        }

        try {
            setIsSubmitting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/${articleId}/notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    // Store tweet URL in noteImageUrl — it's the xUrl for ArticleNote
                    noteText: twitterText.trim() || ' ',
                    importantFlag: 0,
                    noteType: 'xtra',
                    noteImageUrl: twitterUrl.trim()
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('X/Twitter post added!');
                setTwitterUrl('');
                setTwitterText('');
                setTwitterUrlError('');
                setShowTwitterModal(false);
                if (onNoteAdded) onNoteAdded();
            } else {
                toast.error(data.message || 'Failed to add X/Twitter post');
            }
        } catch (error) {
            console.error('Error adding Twitter note:', error);
            toast.error('Failed to add X/Twitter post');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseTwitterModal = () => {
        if (isSubmitting) return;
        setTwitterUrl('');
        setTwitterText('');
        setTwitterUrlError('');
        setShowTwitterModal(false);
    };

    return (
        <>
            <div className="space-y-2">
                <button
                    onClick={() => setShowNoteModal(true)}
                    className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all bg-gray-200 text-gray-800 hover:bg-gray-300 flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                >
                    <span>Add Note</span>
                </button>

                <button
                    onClick={() => setShowImportantModal(true)}
                    className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all bg-gray-200 text-gray-800 hover:bg-gray-300 flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                >
                    <span>Add Important Note</span>
                </button>

                <button
                    onClick={() => setShowDiscordModal(true)}
                    className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all bg-[#5865F2] text-white hover:bg-[#4752C4] flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                >
                    <span>Add Discord Details</span>
                </button>

                <button
                    onClick={() => setShowTwitterModal(true)}
                    className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all bg-black text-white hover:bg-gray-900 flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.264 5.632 5.9-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span>Add X/Twitter Post</span>
                </button>
            </div>

            {/* Add Note Modal */}
            {showNoteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-offwhite border-2 border-gray-800 rounded p-6 w-full max-w-md shadow-lg">
                        <h2 className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-coal_dark mb-4">
                            Add Note
                        </h2>
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Enter your note here..."
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-maroon resize-none mb-4"
                            rows="6"
                            disabled={isSubmitting}
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setShowNoteModal(false)}
                                disabled={isSubmitting}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAddNote(false)}
                                disabled={isSubmitting}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                            >
                                {isSubmitting ? 'Adding...' : 'Add Note'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Important Note Modal */}
            {showImportantModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-offwhite border-2 border-gray-800 rounded p-6 w-full max-w-md shadow-lg">
                        <h2 className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-coal_dark mb-4">
                            Add Important Note
                        </h2>
                        <textarea
                            value={importantText}
                            onChange={(e) => setImportantText(e.target.value)}
                            placeholder="Enter your important note here..."
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-maroon resize-none mb-4"
                            rows="6"
                            disabled={isSubmitting}
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setShowImportantModal(false)}
                                disabled={isSubmitting}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAddNote(true)}
                                disabled={isSubmitting}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                            >
                                {isSubmitting ? 'Adding...' : 'Add Note'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Discord Details Modal */}
            {showDiscordModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div
                        className="bg-offwhite border-2 border-gray-800 w-full max-w-lg shadow-lg"
                        style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)' }}
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-gray-800 bg-[#5865F2]">
                            <svg className="w-5 h-5 text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.001.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                            </svg>
                            <h2 className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-white">
                                Add Discord Details
                            </h2>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                                    Screenshot <span className="text-red-500">*</span>
                                </label>
                                {discordImagePreview ? (
                                    <div className="relative">
                                        <div className="border-2 border-[#7289DA] rounded overflow-hidden bg-[#E9ECFF] max-h-48 flex items-center justify-center">
                                            <img src={discordImagePreview} alt="Preview" className="max-w-full max-h-48 object-contain" />
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            {isUploadingImage ? (
                                                <span className="text-xs text-[#5865F2] font-['Jersey_10'] uppercase tracking-wide">Uploading...</span>
                                            ) : discordImageUrl ? (
                                                <span className="text-xs text-green-600 font-['Jersey_10'] uppercase tracking-wide">✓ Uploaded</span>
                                            ) : (
                                                <span className="text-xs text-red-500 font-['Jersey_10'] uppercase tracking-wide">Upload failed</span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setDiscordImageFile(null);
                                                    setDiscordImagePreview(null);
                                                    setDiscordImageUrl(null);
                                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                                }}
                                                className="text-xs text-gray-500 hover:text-red-600 font-['Jersey_10'] uppercase tracking-wide transition-colors"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full border-2 border-dashed border-[#7289DA] rounded bg-[#E9ECFF] hover:bg-[#dde1ff] transition-colors py-6 flex flex-col items-center gap-2"
                                    >
                                        <svg className="w-8 h-8 text-[#5865F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                                        </svg>
                                        <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-[#5865F2]">Click to upload screenshot</span>
                                        <span className="text-[11px] text-gray-500">JPEG, PNG, GIF or WebP — max 5MB</span>
                                    </button>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={handleDiscordImageChange}
                                    className="hidden"
                                />
                            </div>

                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={discordText}
                                    onChange={(e) => setDiscordText(e.target.value)}
                                    placeholder="Describe the Discord details, event info, or any relevant context..."
                                    className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-[#5865F2] resize-none text-sm"
                                    rows="4"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end px-5 pb-5">
                            <button
                                onClick={handleCloseDiscordModal}
                                disabled={isSubmitting || isUploadingImage}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDiscordSubmit}
                                disabled={isSubmitting || isUploadingImage || !discordImageUrl || !discordText.trim()}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-5 py-2 bg-[#5865F2] text-white hover:bg-[#4752C4] transition-colors rounded disabled:opacity-40"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                            >
                                {isSubmitting ? 'Adding...' : isUploadingImage ? 'Uploading...' : 'Add Discord Details'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add X/Twitter Post Modal */}
            {showTwitterModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div
                        className="bg-offwhite border-2 border-gray-800 w-full max-w-lg shadow-lg"
                        style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)' }}
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-gray-800 bg-black">
                            <svg className="w-5 h-5 text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.264 5.632 5.9-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            <h2 className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-white">
                                Add X/Twitter Post
                            </h2>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                                    Post URL <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    value={twitterUrl}
                                    onChange={handleTwitterUrlChange}
                                    placeholder="https://x.com/username/status/1234567890"
                                    className={`w-full p-3 border-2 rounded focus:outline-none text-sm transition-colors ${
                                        twitterUrlError
                                            ? 'border-red-400 focus:border-red-500'
                                            : twitterUrl && !twitterUrlError
                                                ? 'border-green-400 focus:border-green-500'
                                                : 'border-gray-300 focus:border-black'
                                    }`}
                                    disabled={isSubmitting}
                                />
                                {twitterUrlError && (
                                    <p className="mt-1 text-[11px] text-red-500 font-['Jersey_10'] uppercase tracking-wide">
                                        {twitterUrlError}
                                    </p>
                                )}
                                {twitterUrl && !twitterUrlError && (
                                    <p className="mt-1 text-[11px] text-green-600 font-['Jersey_10'] uppercase tracking-wide">
                                        ✓ Valid URL
                                    </p>
                                )}
                                <p className="mt-1 text-[11px] text-gray-400">
                                    Accepts x.com or twitter.com post links
                                </p>
                            </div>

                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-2">
                                    Additional Context{' '}
                                    <span className="text-gray-400 normal-case font-normal">(optional)</span>
                                </label>
                                <textarea
                                    value={twitterText}
                                    onChange={(e) => setTwitterText(e.target.value)}
                                    placeholder="Add any extra context or notes about this post..."
                                    className="w-full p-3 border-2 border-gray-300 rounded focus:outline-none focus:border-black resize-none text-sm"
                                    rows="3"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {twitterUrl && !twitterUrlError && (
                                <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2">
                                    <p className="text-[11px] text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                                        The post will be embedded on the article page
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 justify-end px-5 pb-5">
                            <button
                                onClick={handleCloseTwitterModal}
                                disabled={isSubmitting}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleTwitterSubmit}
                                disabled={isSubmitting || !twitterUrl.trim() || !!twitterUrlError}
                                className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-5 py-2 bg-black text-white hover:bg-gray-800 transition-colors rounded disabled:opacity-40"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                            >
                                {isSubmitting ? 'Adding...' : 'Add Post'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default StaffArticleActions;